import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Database,
  Loader2,
  Menu,
  MessageCircle,
  MessageSquarePlus,
  Send,
  Share,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Link2,
  CalendarDays,
  Zap,
  Mic,
  Type,
  ArrowRight,
} from 'lucide-react';
import ConversationMessageBubble from './ConversationMessageBubble';
import FeedbackModifierCard from './feedback/FeedbackModifierCard';
import Sidebar from './Sidebar';
import { useAdminApi } from '../hooks/useAdminApi';
import type { ConversationSummary, FeedbackChatData, FeedbackChatMessage, FeedbackReference } from '../hooks/useAdminApi';
import { startWordByWordReveal } from '../utils/startWordByWordReveal';
import { setupOrAdminAuthHeaders } from '../utils/workspace';

interface Source {
  document: string;
  similarity: number;
  chunk_id: string;
  preview: string;
}

interface AgentAction {
  type: string;
  label: string;
  details?: Record<string, any>;
  ts?: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  actions?: AgentAction[];
}

interface Conversation {
  chat_id: string;
  messages: Message[];
  created_at?: string;
  last_modified?: string;
  customer_slug?: string | null;
  workspace_slug?: string | null;
  agent_slug?: string | null;
  origin_mode?: 'voice' | 'text' | null;
  switched_to_text?: boolean;
  switched_at_index?: number | null;
}

interface ConversationViewProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

const hasVisibleModifierText = (value: string | undefined): boolean => {
  const text = (value || '').trim();
  return text !== '' && text !== '""' && text !== "''" && text !== '"' && text !== "'";
};

const ConversationView: React.FC<ConversationViewProps> = ({ isDarkMode, onToggleTheme }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const conversationCustomerParam = searchParams.get('customer') || searchParams.get('customer_slug') || '';
  const conversationWorkspaceParam = searchParams.get('workspace') || searchParams.get('workspace_slug') || '';
  const conversationAgentParam = searchParams.get('agent') || searchParams.get('agent_slug') || '';
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isFeedbackMode = searchParams.get('feedback') !== null;
  const adminApi = useAdminApi();
  const {
    isAuthenticated: isAdminAuthenticated,
    loading: isAdminLoading,
    error: adminApiError,
    login: adminLogin,
    username: adminUsername,
    getAdminProfile,
    getConversations,
    deleteConversation,
    getFeedbackChat,
    startFeedbackChat,
    resetFeedbackChat,
    sendFeedbackChatMessage,
    updateFeedbackModifier,
  } = adminApi;
  const [adminEmail, setAdminEmail] = useState<string | null>(null);

  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const [feedbackChat, setFeedbackChat] = useState<FeedbackChatData | null>(null);
  const [feedbackChatLoading, setFeedbackChatLoading] = useState(false);
  const [feedbackActionError, setFeedbackActionError] = useState<string | null>(null);
  const [feedbackInput, setFeedbackInput] = useState('');
  const [composerReferences, setComposerReferences] = useState<FeedbackReference[]>([]);
  const [modifierBusyMessageId, setModifierBusyMessageId] = useState<string | null>(null);
  const feedbackMessagesEndRef = useRef<HTMLDivElement>(null);
  const introTypingCancelRef = useRef<(() => void) | null>(null);
  const [typingIntroMessageId, setTypingIntroMessageId] = useState<string | null>(null);
  const [typingIntroVisibleText, setTypingIntroVisibleText] = useState('');
  const [showTypingDots, setShowTypingDots] = useState(false);
  const [pendingFeedbackUserMessage, setPendingFeedbackUserMessage] = useState<FeedbackChatMessage | null>(null);
  const [showPendingFeedbackAssistantDots, setShowPendingFeedbackAssistantDots] = useState(false);
  const [agentDisplayName, setAgentDisplayName] = useState('Sally');
  const [feedbackNavConversations, setFeedbackNavConversations] = useState<ConversationSummary[]>([]);
  const feedbackInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isAdminAuthenticated) return;
    getAdminProfile().then(p => { if (p) setAdminEmail(p.email ?? null); });
  }, [isAdminAuthenticated, getAdminProfile]);

  useEffect(() => {
    const fetchConversation = async () => {
      if (!uuid) {
        setError('Invalid conversation ID');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const ConversationService = (await import('../services/conversationService')).default;
        const service = ConversationService.getInstance();
        let data = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            data = await service.getConversation(uuid, {
              customerSlug: conversationCustomerParam || null,
              workspaceSlug: conversationWorkspaceParam || null,
              agentSlug: conversationAgentParam || null,
            });
            break;
          } catch (err) {
            const isRetryable404 = err instanceof Error && err.message.includes('404') && attempt < 2;
            if (!isRetryable404) {
              throw err;
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }
        setConversation(data);
      } catch (err) {
        console.error('Error loading conversation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
  }, [conversationAgentParam, conversationCustomerParam, conversationWorkspaceParam, uuid]);

  useEffect(() => {
    if (!isFeedbackMode || !isAdminAuthenticated) {
      return;
    }

    let isMounted = true;
    const loadFeedbackNavigation = async () => {
      const result = await getConversations();
      if (isMounted && result) {
        setFeedbackNavConversations(result);
      }
    };

    loadFeedbackNavigation();
    return () => {
      isMounted = false;
    };
  }, [getConversations, isAdminAuthenticated, isFeedbackMode, uuid]);

  useEffect(() => {
    if (!isFeedbackMode || !isAdminAuthenticated || !uuid) {
      return;
    }

    let isMounted = true;
    const loadFeedbackChat = async () => {
      setFeedbackChatLoading(true);
      const data = await getFeedbackChat(uuid);
      if (isMounted && data) {
        const hasOnlyInitialAssistantMessage =
          Array.isArray(data.messages) &&
          data.messages.length === 1 &&
          data.messages[0]?.role === 'assistant';
        setFeedbackChat(hasOnlyInitialAssistantMessage ? null : data);
      }
      if (isMounted) {
        setFeedbackChatLoading(false);
      }
    };

    loadFeedbackChat();
    return () => {
      isMounted = false;
    };
  }, [isFeedbackMode, isAdminAuthenticated, getFeedbackChat, uuid]);

  useEffect(() => {
    const profileCustomerSlug = conversation?.customer_slug || conversation?.workspace_slug;
    if (!profileCustomerSlug) {
      setAgentDisplayName('Sally');
      return;
    }

    let isMounted = true;
    const loadWorkspaceAgentName = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/customer/workspace/${encodeURIComponent(profileCustomerSlug)}`,
          { headers: setupOrAdminAuthHeaders(profileCustomerSlug) },
        );
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        if (!isMounted) {
          return;
        }
        const effectiveAgentName = typeof data?.effectiveAgentName === 'string' ? data.effectiveAgentName.trim() : '';
        const configuredAgentName = typeof data?.profileSections?.agent_name === 'string' ? data.profileSections.agent_name.trim() : '';
        setAgentDisplayName(effectiveAgentName || configuredAgentName || 'Sally');
      } catch {
        if (isMounted) {
          setAgentDisplayName('Sally');
        }
      }
    };

    loadWorkspaceAgentName();
    return () => {
      isMounted = false;
    };
  }, [conversation?.customer_slug, conversation?.workspace_slug]);

  useEffect(() => {
    if (!feedbackChat?.messages?.length && !pendingFeedbackUserMessage && !showPendingFeedbackAssistantDots) {
      return;
    }
    feedbackMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [feedbackChat, pendingFeedbackUserMessage, showPendingFeedbackAssistantDots]);

  useEffect(() => {
    return () => {
      introTypingCancelRef.current?.();
      introTypingCancelRef.current = null;
    };
  }, []);

  const canSendFeedbackMessage = feedbackInput.trim().length > 0;

  const currentConversationIndex = useMemo(() => (
    feedbackNavConversations.findIndex((item) => item.chat_id === uuid)
  ), [feedbackNavConversations, uuid]);

  const previousConversation = currentConversationIndex > 0
    ? feedbackNavConversations[currentConversationIndex - 1]
    : null;
  const nextConversation = currentConversationIndex >= 0 && currentConversationIndex < feedbackNavConversations.length - 1
    ? feedbackNavConversations[currentConversationIndex + 1]
    : null;
  const feedbackConversationCounter = currentConversationIndex >= 0
    ? `${currentConversationIndex + 1}/${feedbackNavConversations.length}`
    : feedbackNavConversations.length > 0
      ? `?/${feedbackNavConversations.length}`
      : '';

  const buildConversationUrl = useCallback((target: ConversationSummary, feedback: boolean) => {
    const params = new URLSearchParams();
    if (feedback) params.set('feedback', '1');
    if (target.customer_slug) params.set('customer', target.customer_slug);
    if (target.workspace_slug) params.set('workspace', target.workspace_slug);
    if (target.agent_slug) params.set('agent', target.agent_slug);
    const queryString = params.toString();
    return `/c/${target.chat_id}${queryString ? `?${queryString}` : ''}`;
  }, []);

  const navigateToFeedbackConversation = useCallback((target: ConversationSummary | null | undefined) => {
    if (!target) {
      return;
    }
    navigate(buildConversationUrl(target, true));
  }, [buildConversationUrl, navigate]);

  const handleDeleteCurrentConversation = useCallback(async () => {
    if (!uuid) return;
    const confirmed = window.confirm('Delete this conversation permanently?');
    if (!confirmed) return;

    const fallbackTarget = nextConversation || previousConversation;
    const ok = await deleteConversation(uuid);
    if (!ok) {
      setFeedbackActionError(adminApiError || 'Conversation could not be deleted.');
      return;
    }

    setFeedbackNavConversations(prev => prev.filter(item => item.chat_id !== uuid));
    if (fallbackTarget) {
      navigate(buildConversationUrl(fallbackTarget, isFeedbackMode));
    } else {
      navigate('/admin?tab=conversations');
    }
  }, [adminApiError, buildConversationUrl, deleteConversation, isFeedbackMode, navigate, nextConversation, previousConversation, uuid]);

  const handleFeedbackLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    const ok = await adminLogin(loginUsername, loginPassword);
    setLoginLoading(false);
    if (!ok) {
      setLoginError('Login fehlgeschlagen. Bitte prüfen Sie Ihre Zugangsdaten.');
    }
  };

  const handleStartFeedbackChat = async () => {
    if (!uuid) return;
    setFeedbackActionError(null);
    setFeedbackChatLoading(true);
    const data = await startFeedbackChat(uuid);
    setFeedbackChatLoading(false);
    if (data) {
      setFeedbackChat(data);
      const firstMessage = data.messages?.[0];
      if (firstMessage && firstMessage.role === 'assistant') {
        introTypingCancelRef.current?.();
        introTypingCancelRef.current = null;

        setTypingIntroMessageId(firstMessage.id);
        setTypingIntroVisibleText('');
        setShowTypingDots(true);

        introTypingCancelRef.current = startWordByWordReveal(firstMessage.content || '', {
          initialDelayMs: 2000,
          wordDelayMs: 60,
          onBeforeStart: () => {
            setShowTypingDots(false);
          },
          onUpdate: (nextText) => {
            setTypingIntroVisibleText(nextText);
          },
          onComplete: () => {
            introTypingCancelRef.current = null;
            setTypingIntroMessageId(null);
            setTypingIntroVisibleText('');
          },
        });
      }
    } else {
      setFeedbackActionError(adminApiError || 'Feedbackgespräch konnte nicht gestartet werden.');
    }
  };

  const buildReferenceForMessage = (messageIndex: number): FeedbackReference | null => {
    if (!conversation) {
      return null;
    }
    const message = conversation.messages[messageIndex];
    if (!message) {
      return null;
    }

    const selection = window.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const messageNodes = Array.from(document.querySelectorAll('[data-message-index]'));
    const intersectingMessageIndices = range
      ? messageNodes
          .filter((node) => {
            try {
              return range.intersectsNode(node);
            } catch {
              return false;
            }
          })
          .map((node) => Number((node as HTMLElement).dataset.messageIndex))
          .filter((index) => Number.isInteger(index) && index >= 0)
      : [];

    const isMultiMessageSelection = intersectingMessageIndices.length > 1;
    const selectedText = !isMultiMessageSelection ? (selection?.toString().trim() || '') : '';
    selection?.removeAllRanges();

    if (isMultiMessageSelection) {
      const combinedMessages = intersectingMessageIndices
        .map((index) => {
          const selectedMessage = conversation.messages[index];
          if (!selectedMessage?.content) {
            return null;
          }
          const roleLabel = selectedMessage.role === 'user' ? 'User' : 'Assistant';
          return `${roleLabel}: ${selectedMessage.content}`;
        })
        .filter((value): value is string => Boolean(value));

      return {
        id: `${messageIndex}-${Date.now()}`,
        sourceMessageIndex: intersectingMessageIndices[0] ?? messageIndex,
        sourceRole: 'mixed',
        selectedText: combinedMessages.join('\n'),
        sourceContent: combinedMessages.join('\n'),
      };
    }

    return {
      id: `${messageIndex}-${Date.now()}`,
      sourceMessageIndex: messageIndex,
      sourceRole: message.role,
      selectedText: selectedText || message.content,
      sourceContent: message.content,
    };
  };

  const handleAddReference = (messageIndex: number) => {
    const reference = buildReferenceForMessage(messageIndex);
    if (!reference) {
      return;
    }
    setComposerReferences([reference]);
    window.setTimeout(() => {
      feedbackInputRef.current?.focus();
    }, 0);
  };

  const handleRemoveReference = (referenceId: string | undefined) => {
    setComposerReferences((prev) => prev.filter((reference) => reference.id !== referenceId));
  };

  const getReferenceRoleLabel = (reference: FeedbackReference) => {
    if (reference.sourceRole === 'mixed') {
      return 'Verlauf';
    }
    return reference.sourceRole === 'user' ? 'User' : 'Assistant';
  };

  const handleSendFeedbackMessage = async () => {
    if (!uuid || !canSendFeedbackMessage) {
      return;
    }

    setFeedbackActionError(null);
    const optimisticUserMessage: FeedbackChatMessage = {
      id: `pending-user-${Date.now()}`,
      role: 'user',
      content: feedbackInput.trim(),
      created_at: new Date().toISOString(),
      references: composerReferences,
    };
    setPendingFeedbackUserMessage(optimisticUserMessage);
    setShowPendingFeedbackAssistantDots(true);
    setFeedbackInput('');
    setComposerReferences([]);

    const result = await sendFeedbackChatMessage(uuid, optimisticUserMessage.content, optimisticUserMessage.references || []);
    if (!result) {
      setPendingFeedbackUserMessage(null);
      setShowPendingFeedbackAssistantDots(false);
      setFeedbackActionError(adminApiError || 'Feedback-Nachricht konnte nicht gesendet werden.');
      return;
    }

    console.log('[FeedbackChat] consumed action logs for next LLM turn:', result.debug_consumed_action_logs || []);

    setPendingFeedbackUserMessage(null);
    setShowPendingFeedbackAssistantDots(false);
    setFeedbackChat(result.feedback_chat);
  };

  const handleFeedbackInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isAdminLoading && canSendFeedbackMessage) {
        handleSendFeedbackMessage();
      }
    }
  };

  const handleEditModifier = async (messageId: string, nextText: string) => {
    if (!uuid) return;
    setModifierBusyMessageId(messageId);
    setFeedbackActionError(null);
    const result = await updateFeedbackModifier(uuid, messageId, 'edit', nextText);
    setModifierBusyMessageId(null);
    if (!result) {
      setFeedbackActionError(adminApiError || 'Modifier konnte nicht bearbeitet werden.');
      return;
    }
    console.log('[FeedbackChat] pending action logs after modifier edit:', result.debug_pending_action_logs || []);
    setFeedbackChat(result.feedback_chat);
  };

  const handleAcceptModifier = async (messageId: string) => {
    if (!uuid) return;
    setModifierBusyMessageId(messageId);
    setFeedbackActionError(null);
    const result = await updateFeedbackModifier(uuid, messageId, 'accept');
    setModifierBusyMessageId(null);
    if (!result) {
      setFeedbackActionError(adminApiError || 'Modifier konnte nicht angenommen werden.');
      return;
    }
    console.log('[FeedbackChat] pending action logs after modifier accept:', result.debug_pending_action_logs || []);
    setFeedbackChat(result.feedback_chat);
  };

  const handleResetFeedbackChat = async () => {
    if (!uuid) {
      return;
    }
    const ok = await resetFeedbackChat(uuid);
    if (!ok) {
      setFeedbackActionError(adminApiError || 'Feedbackgespräch konnte nicht zurückgesetzt werden.');
      return;
    }
    setFeedbackChat(null);
    setPendingFeedbackUserMessage(null);
    setShowPendingFeedbackAssistantDots(false);
    setComposerReferences([]);
    setFeedbackInput('');
    setFeedbackActionError(null);
  };

  useEffect(() => {
    if (!isFeedbackMode) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === 'input'
        || tagName === 'textarea'
        || tagName === 'select'
        || target?.isContentEditable
      ) {
        return;
      }

      if (event.key === 'ArrowLeft' && previousConversation) {
        event.preventDefault();
        navigateToFeedbackConversation(previousConversation);
      } else if (event.key === 'ArrowRight' && nextConversation) {
        event.preventDefault();
        navigateToFeedbackConversation(nextConversation);
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        handleDeleteCurrentConversation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDeleteCurrentConversation, isFeedbackMode, navigateToFeedbackConversation, nextConversation, previousConversation]);

  const feedbackChatMessages = useMemo<FeedbackChatMessage[]>(() => {
    const baseMessages = feedbackChat?.messages || [];
    const mergedMessages = [...baseMessages];
    if (pendingFeedbackUserMessage) {
      mergedMessages.push(pendingFeedbackUserMessage);
    }
    return mergedMessages;
  }, [feedbackChat, pendingFeedbackUserMessage]);

  if (!uuid) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
          <p>Loading conversation...</p>
        </div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-center">
          <p className="text-red-500">{error || 'Conversation not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isFeedbackMode ? 'h-screen overflow-hidden' : 'min-h-screen'} ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {isAdminAuthenticated && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isDarkMode={isDarkMode}
          onToggleTheme={onToggleTheme}
          onStartNewChat={() => {
            setSidebarOpen(false);
            navigate('/');
          }}
          adminUsername={adminUsername}
          adminEmail={adminEmail}
        />
      )}
      <div className={`flex-1 flex ${isFeedbackMode ? 'h-full' : ''}`}>
        {isFeedbackMode && isAdminAuthenticated && (
          <>
            <button
              type="button"
              onClick={() => navigateToFeedbackConversation(previousConversation)}
              disabled={!previousConversation}
              className={`fixed left-3 top-1/2 z-20 -translate-y-1/2 rounded-full border p-3 shadow-lg transition-all ${
                !previousConversation
                  ? 'opacity-25 cursor-not-allowed'
                  : isDarkMode
                    ? 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="Previous conversation"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              onClick={() => navigateToFeedbackConversation(nextConversation)}
              disabled={!nextConversation}
              className={`fixed right-3 top-1/2 z-20 -translate-y-1/2 rounded-full border p-3 shadow-lg transition-all ${
                !nextConversation
                  ? 'opacity-25 cursor-not-allowed'
                  : isDarkMode
                    ? 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title="Next conversation"
            >
              <ChevronRight size={22} />
            </button>
            {feedbackConversationCounter && (
              <div className={`fixed bottom-4 left-4 z-20 rounded-full border px-3 py-1.5 text-xs font-mono shadow-lg ${
                isDarkMode
                  ? 'border-gray-700 bg-gray-800 text-gray-300'
                  : 'border-gray-200 bg-white text-gray-600'
              }`}>
                {feedbackConversationCounter}
              </div>
            )}
          </>
        )}
        <div className={`${isFeedbackMode ? 'w-1/2 px-8 overflow-y-auto h-full' : 'max-w-4xl mx-auto px-4'} py-8`}>
          <div className="mb-6 pl-14 sm:pl-16">
            <div className="flex items-center justify-between">
              <div>
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Gespräch {isFeedbackMode && <span className="text-sm font-normal text-blue-500 ml-2">(Feedback)</span>}
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {conversation.messages.length} Nachrichten
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isAdminAuthenticated && (
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className={`fixed top-4 left-4 p-2.5 rounded-lg transition-colors z-10 ${
                      isDarkMode
                        ? 'hover:bg-gray-700 text-gray-300'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                    title="Zur Synaworks.ai"
                  >
                    <Menu size={20} />
                  </button>
                )}
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/c/${uuid}`;
                    if (navigator.clipboard && window.isSecureContext) {
                      navigator.clipboard.writeText(url).catch(() => {});
                    }
                    setShareCopied(true);
                    setTimeout(() => setShareCopied(false), 2000);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isDarkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Share size={16} />
                  {shareCopied ? 'Kopiert!' : 'Teilen'}
                </button>
                {isAdminAuthenticated && (
                  <button
                    onClick={handleDeleteCurrentConversation}
                    disabled={isAdminLoading}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDarkMode ? 'hover:bg-red-950/40 text-red-300 hover:text-red-200' : 'hover:bg-red-50 text-red-600 hover:text-red-700'
                    } disabled:opacity-50`}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                )}
                {!isFeedbackMode && isAdminAuthenticated && (
                  <button
                    onClick={() => navigate(`/c/${uuid}?feedback`)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDarkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <MessageCircle size={16} />
                    Feedback
                  </button>
                )}
                {isFeedbackMode && (
                  <button
                    onClick={() => navigate('/admin?tab=conversations')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isDarkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <MessageCircle size={16} />
                    Feedback schließen
                  </button>
                )}
              </div>
            </div>
          </div>

          {conversation.origin_mode && (
            <div className="mb-3 flex justify-center">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  isDarkMode
                    ? 'bg-gray-800 text-gray-300 ring-1 ring-gray-700'
                    : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
                }`}
              >
                {conversation.origin_mode === 'voice' ? (
                  <><Mic size={12} /> Sprachchat</>
                ) : (
                  <><Type size={12} /> Textchat</>
                )}
                {conversation.switched_to_text && (
                  <><ArrowRight size={12} className="opacity-60" /> <Type size={12} /> in Text fortgesetzt</>
                )}
              </span>
            </div>
          )}

          <div className="space-y-4">
            {conversation.messages.map((message, index) => (
              <React.Fragment key={index}>
              {conversation.switched_to_text &&
                conversation.switched_at_index != null &&
                index === conversation.switched_at_index && (
                <div className="flex items-center gap-3 py-1" data-switch-divider>
                  <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Type size={12} /> In Text fortgesetzt
                  </span>
                  <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              )}
              <div data-message-index={index} className="relative group">
                {(message.content?.trim() || message.role === 'user') && (
                  <ConversationMessageBubble
                    content={message.content}
                    isUser={message.role === 'user'}
                    isDarkMode={isDarkMode}
                  />
                )}
                {/* Agent actions — tool calls the agent itself selected */}
                {message.role === 'assistant' && message.actions && message.actions.length > 0 && (
                  <div className="mt-1 ml-10 sm:ml-12 flex flex-wrap gap-1.5">
                    {message.actions.map((action, i) => {
                      const ActionIcon = action.type === 'show_link'
                        ? Link2
                        : action.type === 'calendar'
                        ? CalendarDays
                        : Zap;
                      return (
                        <span
                          key={i}
                          title={action.details?.url || action.type}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            isDarkMode
                              ? 'bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-500/30'
                              : 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200'
                          }`}
                        >
                          <ActionIcon size={12} />
                          {action.label}
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* RAG Sources — visible to authenticated admins */}
                {message.role === 'assistant' && message.sources && message.sources.length > 0 && isAdminAuthenticated && (
                  <details className={`mt-1 ml-10 sm:ml-12 rounded-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <summary className="flex items-center gap-1.5 text-xs cursor-pointer select-none hover:underline">
                      <Database size={12} />
                      {message.sources.length} Quelle{message.sources.length !== 1 ? 'n' : ''}
                    </summary>
                    <div className="mt-1.5 space-y-1.5">
                      {message.sources.map((src, i) => (
                        <div
                          key={src.chunk_id || i}
                          className={`text-xs p-2.5 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              {src.document}
                            </span>
                            <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono ${
                              src.similarity >= 0.8
                                ? 'bg-green-100 text-green-700'
                                : src.similarity >= 0.6
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {(src.similarity * 100).toFixed(1)}%
                            </span>
                          </div>
                          {src.preview && (
                            <p className={`mt-1 leading-relaxed line-clamp-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {src.preview}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {isFeedbackMode && isAdminAuthenticated && (
                  <button
                    onClick={() => handleAddReference(index)}
                    className={`absolute top-2 right-2 p-1.5 rounded-full transition-all opacity-0 group-hover:opacity-100 ${
                      isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    title="Als Referenz in das Feedback übernehmen"
                  >
                    <MessageSquarePlus size={16} />
                  </button>
                )}
              </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {isFeedbackMode && (
          <div className={`w-1/2 h-full border-l ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} flex flex-col`}>
            {!isAdminAuthenticated ? (
              <div className="flex flex-col items-center justify-center h-full max-w-sm mx-auto px-6">
                <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Admin Login</h2>
                <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Melden Sie sich an, um das Feedbackgespräch zu führen.</p>
                <form onSubmit={handleFeedbackLogin} className="w-full space-y-3">
                  <input
                    type="text"
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    placeholder="Benutzername"
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Passwort"
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                  {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
                  <button
                    type="submit"
                    disabled={loginLoading || !loginUsername || !loginPassword}
                    className="w-full py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                  >
                    {loginLoading && <Loader2 size={16} className="animate-spin" />}
                    Anmelden
                  </button>
                </form>
              </div>
            ) : (
              <>
                <div className={`border-b px-5 py-4 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Feedbackgespräch</h2>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Feedback-Chat mit {agentDisplayName} zum Gespräch `{conversation.chat_id.slice(0, 8)}...`
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleResetFeedbackChat}
                        disabled={isAdminLoading}
                        title="Löscht nur dieses Feedbackgespräch. Bereits angenommene Modifier bleiben bestehen."
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                        } disabled:opacity-50`}
                      >
                        Feedbackgespräch zurücksetzen
                      </button>
                      {conversation.workspace_slug ? (
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${isDarkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                          {conversation.workspace_slug}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                {!feedbackChatMessages.length ? (
                  <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                    <p className={`max-w-md text-sm mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Starte ein separates Feedbackgespräch mit der Agentin. Deine markierten Referenzen kannst du danach wie bei einer Antwortfunktion in deine Nachricht übernehmen.
                    </p>
                    {feedbackActionError && <p className="text-sm text-red-500 mb-3">{feedbackActionError}</p>}
                    <button
                      type="button"
                      onClick={handleStartFeedbackChat}
                      disabled={feedbackChatLoading}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-3 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                      {feedbackChatLoading ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />}
                      Feedbackgespräch starten
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                      {feedbackChatMessages.map((message) => {
                        const references = Array.isArray(message.references) ? message.references : [];
                        const isAnimatedIntro = message.id === typingIntroMessageId;
                        const displayedContent = isAnimatedIntro ? typingIntroVisibleText : message.content;
                        return (
                          <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                              message.role === 'user'
                                ? isDarkMode
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-blue-500 text-white'
                                : isDarkMode
                                  ? 'bg-gray-700 text-gray-100'
                                  : 'bg-gray-100 text-gray-900'
                            }`}>
                              {references.length > 0 && (
                                <div className={`mb-3 space-y-2 rounded-xl border px-3 py-2 ${
                                  message.role === 'user'
                                    ? 'border-white/20 bg-white/10'
                                    : isDarkMode
                                      ? 'border-gray-600 bg-gray-800/80'
                                      : 'border-gray-200 bg-white'
                                }`}>
                                  {references.map((reference, index) => (
                                    <div key={reference.id || `${message.id}-${index}`} className="border-l-2 border-blue-400 pl-3">
                                      <p className="text-[11px] uppercase tracking-[0.08em] opacity-70">
                                        Referenz auf {getReferenceRoleLabel(reference)} · Nachricht {reference.sourceMessageIndex + 1}
                                      </p>
                                      <p className="text-sm italic">{reference.selectedText}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {isAnimatedIntro && showTypingDots ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-current rounded-full animate-bounce" />
                                  <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                  <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                                </div>
                              ) : (
                                <p className="whitespace-pre-wrap text-sm leading-relaxed">{displayedContent}</p>
                              )}
                              {message.modifier && hasVisibleModifierText(message.modifier.text) ? (
                                <FeedbackModifierCard
                                  modifier={message.modifier}
                                  isDarkMode={isDarkMode}
                                  isBusy={modifierBusyMessageId === message.id}
                                  onAccept={() => handleAcceptModifier(message.id)}
                                  onEdit={(nextText) => handleEditModifier(message.id, nextText)}
                                />
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                      {showPendingFeedbackAssistantDots ? (
                        <div className="flex justify-start">
                          <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                            isDarkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-900'
                          }`}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-current rounded-full animate-bounce" />
                              <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                              <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <div ref={feedbackMessagesEndRef} />
                    </div>

                    <div className={`border-t px-5 py-4 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                      {composerReferences.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {composerReferences.map((reference) => (
                            <div
                              key={reference.id}
                              className={`max-w-full rounded-xl border px-3 py-2 text-sm ${
                                isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-100' : 'border-gray-200 bg-gray-50 text-gray-900'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="min-w-0">
                                  <p className={`text-[11px] uppercase tracking-[0.08em] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Referenz auf {getReferenceRoleLabel(reference)} · Nachricht {reference.sourceMessageIndex + 1}
                                  </p>
                                  <p
                                    className="italic overflow-hidden"
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    }}
                                  >
                                    {reference.selectedText}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveReference(reference.id)}
                                  className={`shrink-0 text-xs underline ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  Entfernen
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {feedbackActionError && <p className="mb-2 text-sm text-red-500">{feedbackActionError}</p>}
                      <div className="flex items-end gap-3">
                        <textarea
                          ref={feedbackInputRef}
                          value={feedbackInput}
                          onChange={(e) => setFeedbackInput(e.target.value)}
                          onKeyDown={handleFeedbackInputKeyDown}
                          rows={3}
                          placeholder="Beschreibe der Agentin, was sie sich künftig merken oder anders machen soll."
                          className={`flex-1 resize-none rounded-xl border px-3 py-2 text-sm ${
                            isDarkMode ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={handleSendFeedbackMessage}
                          disabled={!canSendFeedbackMessage || isAdminLoading}
                          className="inline-flex h-[52px] items-center justify-center rounded-xl bg-blue-500 px-4 text-white hover:bg-blue-600 disabled:opacity-50"
                        >
                          {isAdminLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationView;
